import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import apiService, { RateLimitError } from '@/services/apiService';

type PageState = 'form' | 'success';

const COOLDOWN_MS = 5 * 60 * 1000;

function cooldownKey(id: string) {
	return `resume_link_cooldown_${id}`;
}

function getRemainingSeconds(id: string): number {
	const stored = localStorage.getItem(cooldownKey(id));
	if (!stored) return 0;
	const remaining = COOLDOWN_MS - (Date.now() - Number(stored));
	return remaining > 0 ? Math.ceil(remaining / 1000) : 0;
}

function formatTime(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${s.toString().padStart(2, '0')}`;
}

const LinkExpired = (): React.JSX.Element => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const expiredToken = searchParams.get('token') ?? '';

	const [email, setEmail] = useState('');
	const [pageState, setPageState] = useState<PageState>('form');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cooldownSeconds, setCooldownSeconds] = useState(() => (id ? getRemainingSeconds(id) : 0));

	useEffect(() => {
		if (cooldownSeconds <= 0) return;
		const timer = setTimeout(() => {
			setCooldownSeconds((prev) => Math.max(prev - 1, 0));
		}, 1000);
		return () => clearTimeout(timer);
	}, [cooldownSeconds]);

	const startCooldown = () => {
		if (!id) return;
		localStorage.setItem(cooldownKey(id), String(Date.now()));
		setCooldownSeconds(Math.ceil(COOLDOWN_MS / 1000));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!id || !email.trim() || !expiredToken || cooldownSeconds > 0) return;

		setIsSubmitting(true);
		setError(null);

		try {
			await apiService.resumeLink.verifyEmail(id, email.trim(), expiredToken);
			startCooldown();
			setPageState('success');
		} catch (err) {
			if (err instanceof RateLimitError) {
				startCooldown();
			} else {
				setError(t('common.linkExpired.genericError'));
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!id || !expiredToken) {
		return (
			<section className="flex grow items-center">
				<div className="container mx-auto px-4 pb-20">
					<div className="lg:w-5/12 mx-auto text-center">
						<h1 className="text-4xl font-bold mb-6">{t('errors.pageNotFound')}</h1>
						<p className="text-xl mb-10 leading-loose">{t('errors.pageNotFoundDescription')}</p>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="flex grow items-center">
			<div className="container mx-auto px-4 pb-20">
				<div className="max-w-md mx-auto">
					{pageState === 'form' && (
						<div className="bg-background rounded-xl border border-border shadow-sm p-8">
							<h1 className="text-2xl font-bold mb-2">{t('common.linkExpired.title')}</h1>
							<p className="text-muted-foreground mb-8">{t('common.linkExpired.description')}</p>

							<form onSubmit={handleSubmit}>
								<label htmlFor="email" className="block text-sm font-medium mb-2">
									{t('common.linkExpired.emailLabel')}
								</label>
								<Input
									id="email"
									type="email"
									required
									placeholder={t('common.linkExpired.emailPlaceholder')}
									autoComplete="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									disabled={isSubmitting}
								/>
								{error && <p className="mt-2 text-sm text-destructive">{error}</p>}
								<Button
									type="submit"
									className="w-full mt-4"
									disabled={cooldownSeconds > 0}
									loading={isSubmitting}
									loadingText={t('common.linkExpired.sending')}
								>
									{cooldownSeconds > 0
										? t('common.linkExpired.cooldownButton', { time: formatTime(cooldownSeconds) })
										: t('common.linkExpired.submitButton')}
								</Button>
							</form>
						</div>
					)}

					{pageState === 'success' && (
						<div className="bg-background rounded-xl border border-border shadow-sm p-8">
							<h1 className="text-2xl font-bold mb-2">{t('common.linkExpired.successTitle')}</h1>
							<p className="text-muted-foreground">{t('common.linkExpired.successDescription')}</p>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

export default LinkExpired;
